import {
	faArrowRight as fasArrowRight,
	faListUl as fasListUl,
	faScaleBalanced as fasScaleBalanced,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { PureMeasureComponent } from "@component/PureMeasureComponent";
import type { PureMeasureComponentProps, PureMeasureComponentState } from "@component/PureMeasureComponent";
import type { AbstractLeadFormProps } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBankmonitor } from "@ui/fortawesome/bankmonitor";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { getPromoText } from "@utils/product-marketing-text";
import { generateCtUrl } from "@utils/url-ct";
import autobind from "autobind-decorator";
import {
	http,
	Button,
	FormCheck,
	HtmlBlock,
	PromisePool,
	__,
	addParameterToURL,
	getLocale,
	scrollToElement,
	urlPreProcess,
} from "bm-ui-kit";
import compact from "lodash/compact";
import type { ReactNode } from "react";
import { Col, Row } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { Form as FinalForm } from "react-final-form";
import { InView } from "react-intersection-observer";
import type { ProductType } from "../AbstractLeadForm/types";
import { MarketingAccordion } from "./components/MarketingAccordion";
import type { AbstractResponseItem, ButtonIdTarget } from "./types";

export interface AbstractResultItemProps<SearchParams, Request, ResponseItem extends AbstractResponseItem>
	extends PureMeasureComponentProps {
	searchParams: SearchParams;
	request: Request;
	result: ResponseItem;

	index: number;
	selected?: boolean;

	onSelect: (key: number) => void;

	withoutNumber?: boolean;

	compareButtonShow?: boolean;
	compareButtonChecked?: boolean;
	compareButtonDisabled?: boolean;
	onCompare?: (id: string, checked: boolean) => void;

	groupName?: string;
	groupIndex?: number;
	productType: ProductType;

	sendViewItemEvent: Function;
}

export interface AbstractResultItemState extends PureMeasureComponentState {
	feedBackStatus: "interest" | "details" | string;
	wasVisible: boolean;
	clickType?: "reduce-payment" | "interest";
}

export interface AbstractResultItemCompareFormFields {
	compare: boolean;
}

export abstract class AbstractResultItem<
	SearchParams,
	Request,
	ResponseItem extends AbstractResponseItem,
	Props extends AbstractResultItemProps<SearchParams, Request, ResponseItem> = AbstractResultItemProps<
		SearchParams,
		Request,
		ResponseItem
	>,
	State extends AbstractResultItemState = AbstractResultItemState,
> extends PureMeasureComponent<Props, State> {
	private readonly promisePool = new PromisePool();

	protected readonly hasDetailsBlock: boolean = true;
	protected readonly showInterestButton: boolean = true;

	public constructor(props: Props) {
		super(props);

		this.state = this.getInitialState();
	}

	protected getInitialState(): State {
		return {
			feedBackStatus: undefined,
			wasVisible: false,
		} as unknown as State;
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	protected getResultClassName(): string {
		const { result } = this.props;

		return compact([
			"result-item shadow-sm",
			result.bank.code,
			result.marketing?.highlight && "highlight",
			result.marketing?.cssRule,
			result.marketing?.texts?.top && "bm-only",
		]).join(" ");
	}

	public render(): ReactNode {
		const { selected } = this.props;
		const { wasVisible } = this.state;
		const resultClassName = this.getResultClassName();

		return (
			<InView
				as="div"
				className={resultClassName}
				{...GoogleAnalytics4.getInViewProps()}
				skip={wasVisible}
				onChange={this.onVisibilityChange}
			>
				{this.renderHeader()}
				<div className="d-flex flex-wrap justify-content-between align-content-end">
					{this.renderProductData()}
					{this.renderMarketingData()}
					{this.renderButtons()}
				</div>
				{selected && this.renderFeedback()}
			</InView>
		);
	}

	protected renderHeader(name?: string, productLinkClassName = "product-link text-truncate"): ReactNode {
		const { index, result, withoutNumber = false, compareButtonShow } = this.props;
		const isHighlight = result.marketing?.highlight;
		const dataPageUrl = this.getFullDataPageUrl();
		const onClick = result.marketing?.newTargetUrl && isHighlight ? this.onToTheBankClick : this.onHeaderClick;

		return (
			<>
				{this.renderBmPromo()}
				<div className="result-header d-flex align-self-stretch">
					{isHighlight ? (
						<div className="h3 h5 promo">{result.marketing?.texts?.leftSide || "Hirdetés"}</div>
					) : (
						!withoutNumber && <span className="h3 h5 indicator number font-weight-bold">{index}</span>
					)}
					<span className="h3 h5 w-100 overflow-auto">
						<a
							href={dataPageUrl}
							target="_blank"
							onClick={onClick}
							className={productLinkClassName}
							rel="noreferrer"
						>
							{this.renderHeaderTitle(name)}
						</a>
						{this.renderTitleBadge()}
					</span>
					{compareButtonShow && this.renderCompareButton()}
				</div>
			</>
		);
	}

	protected renderHeaderTitle(name?: string): ReactNode {
		const { result } = this.props;
		const resultName = name || result.name;

		return (
			<>
				<strong className="result-name">{resultName}</strong>
				{result?.subType && <span className="result-subtype">{` ${result.subType} `}</span>}
				{result?.bank?.name && <span className="result-bank dash-before">{result.bank.name}</span>}
			</>
		);
	}

	protected renderTitleBadge(): ReactNode {
		return null;
	}

	protected renderCssRulePromo(cssRule: string): ReactNode {
		const promoTexts = getPromoText(cssRule);
		return <div className="bm-only-badge">{promoTexts.listingDesktopText}</div>;
	}

	protected renderBmPromo(): ReactNode {
		const { result } = this.props;

		if (result.marketing?.texts?.top) {
			return (
				<div className="bm-only-badge">
					<FontAwesomeIcon icon={faBankmonitor} className="mr-1" />
					{result.marketing.texts.top}
				</div>
			);
		}

		if (result.marketing?.cssRule) {
			const promoTexts = getPromoText(result.marketing.cssRule);
			if (promoTexts?.listingDesktopText || promoTexts?.listingMobileText) {
				return (
					<div className="product-result-promo">
						{promoTexts.listingIcon && <FontAwesomeIcon icon={promoTexts.listingIcon} className="mr-1" />}
						<span className="d-none d-sm-inline">
							{promoTexts.listingDesktopText || promoTexts.listingMobileText}
						</span>
						{!!promoTexts.listingMobileText && (
							<span className="d-sm-none">{promoTexts.listingMobileText}</span>
						)}
					</div>
				);
			}
		}

		// CSS szabály hozzáadása
		if (result.marketing?.cssRule?.includes("bm-offer")) {
			return (
				<div className="bm-only-badge">
					<FontAwesomeIcon icon={faBankmonitor} className="mr-1" />A Bankmonitor ajánlata
				</div>
			);
		}

		if (result.marketing?.cssRule?.includes("bm-only")) {
			return (
				<div className="bm-only-badge">
					<FontAwesomeIcon icon={faBankmonitor} className="mr-1" />
					<strong>Kizárólag</strong> a Bankmonitornál!
				</div>
			);
		}

		return "";
	}

	protected renderCompareButton(
		label: ReactNode = (
			<>
				<span className="d-xs-none d-lg-inline-block d-none d-xl-inline pr-1">Összehasonlítás</span>
				<FontAwesomeIcon icon={fasScaleBalanced} className="ml-xl-1" />
			</>
		),
	): ReactNode {
		const { compareButtonDisabled = false, compareButtonChecked = false } = this.props;

		return (
			<div className="compare-box compare-product-button ml-auto p-2">
				<FinalForm<AbstractResultItemCompareFormFields>
					initialValues={{ compare: compareButtonChecked }}
					onSubmit={this.onSubmitCompare}
				>
					{({ handleSubmit }) => (
						<Form className="text-white" onSubmit={handleSubmit}>
							<FormCheck
								name="compare"
								label={label}
								disabled={compareButtonDisabled}
								onChange={this.onCompareClick}
								className="m-0"
							/>
						</Form>
					)}
				</FinalForm>
			</div>
		);
	}

	@autobind
	protected onCompareClick(value: boolean): void {
		const {
			onCompare,
			result: { id },
		} = this.props;
		onCompare(id, value);
	}

	@autobind
	protected onSubmitCompare(_form: AbstractResultItemCompareFormFields): void {}

	protected abstract renderProductData(): ReactNode;

	protected renderMarketingData(): ReactNode {
		const { result } = this.props;

		if (!result.marketing?.formattedText?.length) {
			return null;
		}

		return (
			<MarketingAccordion title="Kedvezmények részletei">
				<HtmlBlock className="marketing-text font-weight-light my-2" html={result.marketing?.formattedText} />
			</MarketingAccordion>
		);
	}

	protected renderButtons(): ReactNode {
		const { result, selected } = this.props;
		const { feedBackStatus } = this.state;
		const dataPageUrl = this.getFullDataPageUrl();

		const newTargetUrl = this.getNewTargetUrl();
		const hasGoToTheBank = !!newTargetUrl;
		const hasDetailsButton = getLocale() === "hu_HU" && (!hasGoToTheBank || !result.marketing?.highlight);
		const hasInterestButton = !hasGoToTheBank && this.showInterestButton;

		return (
			<div className="buttons ml-auto mt-auto p-2">
				{hasDetailsButton && (
					<Button
						variant="light"
						className="mt-2"
						icon={<FontAwesomeIcon icon={fasListUl} />}
						href={this.hasDetailsBlock ? undefined : dataPageUrl}
						target="_blank"
						active={selected && feedBackStatus === "details"}
						onClick={this.hasDetailsBlock ? this.onDetailsClick : this.onProductPageClick}
					>
						{__("Részletek")}
					</Button>
				)}
				{hasGoToTheBank && (
					<Button
						className="font-weight-bold text-uppercase ml-2 mt-2"
						href={newTargetUrl}
						icon={<FontAwesomeIcon icon={fasArrowRight} />}
						target="_blank"
						onClick={this.onToTheBankClick}
					>
						{result.marketing?.buttonText || __("Irány a bank!")}
					</Button>
				)}
				{hasInterestButton && (
					<Button
						variant="success"
						className="font-weight-bold text-uppercase ml-2 mt-2"
						active={selected && feedBackStatus === "interest"}
						onClick={() => this.onInterestClick()}
					>
						{__("Érdekel")}
					</Button>
				)}
			</div>
		);
	}

	protected renderFeedback(): ReactNode {
		const { feedBackStatus } = this.state;
		const { result } = this.props;

		if (!feedBackStatus) {
			return;
		}

		return (
			<Row id={`feedback-${result.id}`} className="feedback mx-0">
				{feedBackStatus === "details" && (
					<Col lg={7} className="details block py-3 order-lg-1">
						{this.renderFeedbackDetails()}
					</Col>
				)}
				<Col lg={5} className="lead-block bg-success p-0 order-lg-2">
					{this.renderFeedbackLead(this.getLeadProps())}
				</Col>
				{feedBackStatus === "interest" && (
					<Col lg={7} className="interest block py-3 order-lg-1">
						{this.renderFeedbackInterest()}
					</Col>
				)}
			</Row>
		);
	}

	protected getLeadProps(): AbstractLeadFormProps<any, any, SearchParams> {
		const { searchParams } = this.props;

		return {
			searchParams,
			fluid: true,
			bottomLogo: false,
			ga4Event: {
				buttonId: this.buttonId,
				...this.getCommonEventParameters(),
			},
		};
	}

	protected abstract renderFeedbackLead(props: AbstractLeadFormProps): ReactNode;
	protected abstract renderFeedbackInterest(): ReactNode;
	protected abstract renderFeedbackDetails(): ReactNode;

	@autobind
	protected onHeaderClick(): void {
		this.sendResultButtonEvent("product-name", "localStorageButton");
	}

	@autobind
	protected onDetailsClick(): void {
		const { selected, index, onSelect, result } = this.props;
		const { feedBackStatus } = this.state;

		const isClosing = selected && feedBackStatus === "details";

		if (!isClosing) {
			this.sendResultButtonEvent("details", "saveButton");
		}

		this.setState(
			{
				feedBackStatus: selected && isClosing ? undefined : "details",
			},
			() => {
				if (!isClosing) {
					onSelect(index);
					scrollToElement(`feedback-${result.id}`);
				}
			},
		);
	}

	@autobind
	protected onProductPageClick(): void {
		this.sendResultButtonEvent("product-page", "localStorageButton");
	}

	@autobind
	protected onToTheBankClick(): void {
		this.sendResultButtonEvent("go-to-bank", "localStorageButton");
	}

	@autobind
	protected onInterestClick(buttonType?: string): void {
		const { selected, index, onSelect, result } = this.props;
		const { feedBackStatus } = this.state;

		const isClosing = selected && feedBackStatus === "interest";

		if (!isClosing) {
			this.sendResultButtonEvent(
				`${buttonType === "reduce-payment" ? "reduce-payment" : "interest"}`,
				"saveButton",
			);
		}

		this.setState(
			{
				feedBackStatus: !isClosing ? "interest" : undefined,
			},
			() => {
				if (!isClosing) {
					onSelect(index);
					scrollToElement(`feedback-${result.id}`);
				}
			},
		);
	}

	protected getNewTargetUrl(): string {
		const { result } = this.props;
		const newTargetUrl = result.marketing?.newTargetUrl;

		return newTargetUrl?.length ? generateCtUrl(newTargetUrl) : undefined;
	}

	protected abstract getDataPageUrl(): string;

	protected getFullDataPageUrl(): string {
		const dataPageUrl = this.getDataPageUrl();
		const leadSource = Config.getLeadSource();

		const { result } = this.props;
		const isHighlight = result.marketing?.highlight;

		if (isHighlight) {
			return this.getNewTargetUrl();
		}

		if (leadSource !== "BM") {
			return addParameterToURL(dataPageUrl, {
				source: leadSource,
			});
		}

		return dataPageUrl;
	}

	@autobind
	protected onVisibilityChange(isVisible: boolean): void {
		if (isVisible) {
			const {
				result: { marketing },
				sendViewItemEvent,
			} = this.props;

			if (marketing?.avTrackingCode) {
				http.get(Config.getWpRestApiUrl("/av/"), { url: urlPreProcess(marketing.avTrackingCode) });
			}

			sendViewItemEvent();

			this.setState({
				wasVisible: true,
			});
		}
	}

	protected isSaved(): boolean {
		return false;
	}

	protected sendResultButtonEvent(
		buttonName:
			| "interest"
			| "add-to-favorite"
			| "details"
			| "product-page"
			| "product-name"
			| "go-to-bank"
			| string,
		buttonType: ButtonIdTarget,
	): void {
		const { index } = this.props;
		const {
			result: { id, name, bank },
			groupName = index < 0 ? "promo" : "normal",
			groupIndex = index < 0 ? 0 : 1,
		} = this.props;

		let buttonId = null;

		if (buttonType === "localStorageButton") {
			buttonId = GoogleAnalytics4.setButtonId("bi", this.getSessionStorageName());
		} else if (buttonType === "saveButton") {
			this.buttonId = GoogleAnalytics4.generateId("bi");
			buttonId = this.buttonId;
		} else if (buttonType === "fakeButton") {
			buttonId = GoogleAnalytics4.generateId("bi");
		}

		this.sendEvent("BM_resultItemButton", {
			buttonId,
			groupName,
			groupPosition: groupIndex,
			position: Math.abs(index),
			buttonName,
			providerName: bank?.name,
			productName: name,
			productId: id,
		});
	}

	private getSessionStorageName(): string {
		const { result, productType } = this.props;

		return `button_id_${productType}_${result.id}`;
	}
}
